/**
 * BetProtect — Onboarding context seeder
 *
 * Seeds the learning engine with initial user context collected during
 * the multi-step welcome flow (goals, triggers, exercise preferences).
 */

export interface OnboardingData {
  goals: string[];
  triggers: string[];
  exercisePreferences: string[];
  initialMood?: number;
  initialUrge?: number;
  completedAt: number;
}

const ONBOARDING_KEY = 'bp_onboarding_complete_v1';
const STORAGE_KEY = 'bp_learning_profile_v1';

export function hasCompletedOnboarding(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(ONBOARDING_KEY) === 'true';
}

export function markOnboardingComplete(): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(ONBOARDING_KEY, 'true');
}

/**
 * Seed the learning engine with onboarding context.
 * Maps user-selected triggers to the engine's trigger frequency keys,
 * and boosts exercise category scores based on preferences.
 */
export function seedLearningEngine(data: OnboardingData): void {
  if (typeof window === 'undefined') return;

  let profile: any = { triggerFrequency: {}, avgMood7d: 3, avgUrge7d: 3, lastUpdated: Date.now() };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) profile = { ...profile, ...JSON.parse(raw) };
  } catch {
    // use defaults
  }

  // Map onboarding trigger selections → engine trigger keys
  const TRIGGER_MAP: Record<string, string> = {
    anxiety: 'trigger-anxiety',
    stress: 'trigger-stress',
    boredom: 'trigger-boredom',
    'evening-boredom': 'trigger-boredom-eve',
    loneliness: 'trigger-loneliness',
    financial: 'trigger-financial',
    social: 'trigger-social',
    celebrations: 'trigger-celebration',
    sports: 'trigger-sports',
    ads: 'trigger-ads',
  };

  // Seed trigger frequency from onboarding selections (weight = 2 per selection)
  for (const trigger of data.triggers) {
    const key = TRIGGER_MAP[trigger];
    if (key) {
      profile.triggerFrequency[key] = (profile.triggerFrequency[key] ?? 0) + 2;
    }
  }

  // Map exercise preferences → category boosts
  const PREF_CATEGORY_MAP: Record<string, string[]> = {
    breathing: ['Breathing'],
    meditation: ['Meditation', 'Mindfulness'],
    movement: ['Movement'],
    journaling: ['Reflection'],
    cbt: ['CBT', 'Coping'],
    grounding: ['Grounding'],
    mindset: ['Mindset'],
    quick: ['Breathing', 'Grounding', 'Coping'],
  };

  const preferredCategories = new Set<string>();
  for (const pref of data.exercisePreferences) {
    for (const cat of PREF_CATEGORY_MAP[pref] ?? []) {
      preferredCategories.add(cat);
    }
  }

  // Seed initial mood/urge context if provided
  if (data.initialMood !== undefined) {
    profile.avgMood7d = data.initialMood;
  }
  if (data.initialUrge !== undefined) {
    profile.avgUrge7d = data.initialUrge;
  }

  // Store onboarding metadata on profile for future reference
  profile.onboardingData = {
    goals: data.goals,
    triggers: data.triggers,
    exercisePreferences: data.exercisePreferences,
    preferredCategories: Array.from(preferredCategories),
    completedAt: data.completedAt,
  };

  profile.lastUpdated = Date.now();

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch {
    // ignore quota errors
  }
}
